export interface JsonFieldChange {
  /** Dotted path to the leaf, e.g. `targeting.siteTarget.sites[0].id`. */
  path: string
  /** Absent when the field was added. */
  before?: string
  /** Absent when the field was removed. */
  after?: string
}

/** Flattens a JSON value into its leaves, keyed by dotted path. */
function flatten(value: unknown, path: string, into: Map<string, string>) {
  if (Array.isArray(value)) {
    if (value.length === 0) into.set(path, '[]')
    value.forEach((item, i) => flatten(item, `${path}[${i}]`, into))
  } else if (value && typeof value === 'object') {
    const entries = Object.entries(value)
    if (entries.length === 0 && path) into.set(path, '{}')
    entries.forEach(([key, item]) => flatten(item, path ? `${path}.${key}` : key, into))
  } else {
    into.set(path, typeof value === 'string' ? value : JSON.stringify(value))
  }
}

/** The leaves that differ between two JSON values, in path order. */
export function diffJson(before: unknown, after: unknown): JsonFieldChange[] {
  const a = new Map<string, string>()
  const b = new Map<string, string>()
  flatten(before, '', a)
  flatten(after, '', b)
  const paths = new Set([...a.keys(), ...b.keys()])
  return [...paths]
    .filter((path) => a.get(path) !== b.get(path))
    .sort((x, y) => x.localeCompare(y))
    .map((path) => ({ path, before: a.get(path), after: b.get(path) }))
}

/** Parses JSON, or returns null when there is none or it is unreadable. */
export function parseJsonOrNull(json: string | null | undefined): unknown {
  if (!json) return null
  try {
    return JSON.parse(json)
  } catch {
    return null
  }
}

/** One line of pretty-printed JSON, tagged with the path it prints. */
export interface JsonLine {
  depth: number
  /** Same dotted path `diffJson` reports; the container's path on its brackets. */
  path: string
  key?: string
  /** A leaf value, or an empty container. */
  value?: unknown
  open?: '{' | '['
  close?: '}' | ']'
  comma: boolean
}

/** Pretty-prints JSON into lines that know which field they belong to. */
export function jsonLines(value: unknown): JsonLine[] {
  const out: JsonLine[] = []
  const walk = (v: unknown, path: string, key: string | undefined, depth: number, comma: boolean) => {
    const isArray = Array.isArray(v)
    const entries: Array<[string | undefined, unknown, string]> = isArray
      ? v.map((item, i) => [undefined, item, `${path}[${i}]`])
      : v && typeof v === 'object'
        ? Object.entries(v).map(([k, item]) => [k, item, path ? `${path}.${k}` : k])
        : []
    if (!(v && typeof v === 'object') || entries.length === 0) {
      out.push({ depth, path, key, value: v, comma })
      return
    }
    out.push({ depth, path, key, open: isArray ? '[' : '{', comma: false })
    entries.forEach(([k, item, p], i) => walk(item, p, k, depth + 1, i < entries.length - 1))
    out.push({ depth, path, close: isArray ? ']' : '}', comma })
  }
  walk(value, '', undefined, 0, false)
  return out
}
